///
/// EarthBrowser
///
/// Created by Matt Giger on 9/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Bridges "image" dataset windows to the globe: watches
/// dialog.datasetWindows, creates a GPU texture per window from the
/// bitmap parked in groundOverlayImages, and keeps the shared
/// GroundOverlays slots (bounds, opacity) in sync with the window's
/// imageOverlay state. Adding/removing an overlay recompiles the earth
/// tile shader (slot count is a define); bounds/opacity edits are plain
/// uniform updates.
///

import { type Earth } from "@/earthbrowser/activities/EarthActivity/Earth"
import { boundsToUV, cMaxGroundOverlays, GroundOverlays } from "@/earthbrowser/dataset-distribution/ground-overlay/GroundOverlays"
import { getGroundOverlayImage, removeGroundOverlayImage } from "@/earthbrowser/dataset-distribution/ground-overlay/groundOverlayImages"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { appStore } from "@/redux/store"
import { ActivityBase } from "@stovokor/engine/ActivityBase"
import { Texture } from "@stovokor/shader/Texture"

export class GroundOverlayActivity extends ActivityBase {
	private earth: Earth
	private overlays: GroundOverlays
	private overlayAdded = false
	private unsubscribe?: () => void
	private lastWindows?: unknown

	constructor(context: EarthBrowserContext, earth: Earth) {
		super(context)
		this.earth = earth
		this.overlays = new GroundOverlays(context)
	}

	public start(): void {
		this.unsubscribe = appStore.subscribe(() => this.sync())
		this.sync()
	}

	private sync(): void {
		const windows = appStore.getState().dialog.datasetWindows
		if (windows === this.lastWindows) return
		this.lastWindows = windows

		const imageWindows = Object.values(windows)
			.filter(w => w.windowType === "image" && w.imageOverlay)
		const openIds = new Set(imageWindows.map(w => w.id))
		const prevCount = this.overlays.entries.length

		// Drop overlays whose windows closed and free their GPU + bitmap memory
		for (const entry of this.overlays.entries) {
			if (!openIds.has(entry.id)) {
				entry.texture.destroy()
				removeGroundOverlayImage(entry.id)
			}
		}
		this.overlays.entries = this.overlays.entries.filter(e => openIds.has(e.id))

		// Add new windows (up to the slot cap) and refresh placement uniforms
		for (const window of imageWindows) {
			const state = window.imageOverlay
			if (!state) continue
			const boundsUV = boundsToUV(state.bounds.west, state.bounds.south, state.bounds.east, state.bounds.north)

			const entry = this.overlays.entries.find(e => e.id === window.id)
			if (entry) {
				entry.boundsUV = boundsUV
				entry.opacity = state.opacity
				continue
			}
			if (this.overlays.entries.length >= cMaxGroundOverlays) continue

			// Bitmap is parked before the window opens; if it's missing the
			// window came from a stale/foreign path and can't render
			const image = getGroundOverlayImage(window.id)
			if (!image) continue

			const texture = new Texture(this.context)
			texture.uploadCanvas(image.bitmap, image.bitmap.width, image.bitmap.height)
			this.overlays.entries.push({ id: window.id, texture, boundsUV, opacity: state.opacity })
		}

		// Slot count changes bake a new GROUND_OVERLAY_COUNT into the shader
		if (this.overlays.entries.length !== prevCount) {
			if (this.overlays.entries.length === 0) {
				this.earth.removeOverlay(this.overlays)
				this.overlayAdded = false
			} else if (!this.overlayAdded) {
				this.earth.addOverlay(this.overlays)
				this.overlayAdded = true
			} else {
				this.earth.resetRenderers()
			}
		}
	}

	public override destroy(): void {
		this.unsubscribe?.()
		this.unsubscribe = undefined
		for (const entry of this.overlays.entries) {
			entry.texture.destroy()
			removeGroundOverlayImage(entry.id)
		}
		this.overlays.entries = []
		if (this.overlayAdded) {
			this.earth.removeOverlay(this.overlays)
			this.overlayAdded = false
		}
		super.destroy()
	}
}
